import { formatFileSize, formatFileType } from "@frontend-shared/utils";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";
import { formatDate } from "@/pages/generated-files/utils/format-date";
import { getFileIcon } from "@/pages/generated-files/utils/get-file-icon";

import type { IChatGeneratedFileOptionProps } from "./types";

const META_SEPARATOR = " · ";

// NOTE: NSAI-5312 — one file in the chat's download dialog. The whole row is a
// <label>, so a click anywhere on it ticks the box; a click on the box itself
// is not re-sent by the label, so it never toggles twice.
const ChatGeneratedFileOptionComponent = ({
  file,
  isSelected,
  onToggle,
}: IChatGeneratedFileOptionProps): JSX.Element => {
  const { t } = useTranslation("common");

  const metaLine = useMemo(
    (): string =>
      [
        formatFileType(file.type),
        formatFileSize(file.fileSize ?? undefined),
        formatDate(file.createdAt),
      ]
        .filter((part) => part.length > 0)
        .join(META_SEPARATOR),
    [file.createdAt, file.fileSize, file.type],
  );

  const handleToggle = useCallback((): void => {
    onToggle(file);
  }, [file, onToggle]);

  return (
    <label
      className="flex min-w-0 cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
      data-testid={`chat-generated-file-${file.uuid}`}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={handleToggle}
        aria-label={t("generated_files.archive.select_file", {
          name: file.name,
        })}
        data-testid={`chat-generated-file-select-${file.uuid}`}
      />
      <span className="shrink-0">{getFileIcon(file)}</span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium" title={file.name}>
          {file.name}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {metaLine}
        </span>
      </span>
    </label>
  );
};

export const ChatGeneratedFileOption = memo(ChatGeneratedFileOptionComponent);
